/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import ContentFrame from '../../../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareAppNavStep } from '../../../components/ScrollAware';
import { AppHeaderScrollableText } from '../../../components/appNav/AppHeaderScrollable';

import PasswordStrengthMeter from '../../../components/PasswordStrengthMeter';
import RouteNames from '../../RouteNames';
import PasswordStrength from '../../../../utilities/PasswordStrength';
import ContentWrapper from '../../../components/ContentWrapper';

import { Card, Inputs, Spacer } from '../../../../bphComponents/bundle';

class ChangePassword extends React.Component {

  constructor(props) {
    super(props);
    this.onChangeNewPassword = this.onChangeNewPassword.bind(this);
    this.onSubmitPasswordChange = this.onSubmitPasswordChange.bind(this);
    this.handleOnFocus = this.handleOnFocus.bind(this);

    this.state = {
      isEnteringNewPassword: false,
      isSubmitting: false,
    };
  }

  UNSAFE_componentWillMount() {
    PasswordStrength.requireDependency();
  }

  onChangeNewPassword(value) {
    const strength = PasswordStrength.getScore(value);
    this.props.onChangeNewPassword(value, strength);
  }

  onSubmitPasswordChange(e) {
    e.preventDefault();

    const passwordsValid = this.props.currentPasswordIsValid && this.props.newPasswordIsValid;
    if (!passwordsValid || this.state.isSubmitting) { return; }

    this.setState({ isSubmitting: true });

    this.props.verifyCredentials(this.props.currentCredentials, (verifyError) => {
      if (verifyError) {
        this.setState({ isSubmitting: false });
        return;
      }

      if (this.props.isMfaEnabled) {
        this.props.goToPasswordWithMfa();
      } else {
        this.props.submitPasswordChange((submitError) => {
          this.setState({ isSubmitting: false });
          if (submitError) { return; }
          this.props.finishPasswordChange();
        });
      }
    });
  }

  handleOnFocus() {
    this.props.dismissNotification();
  }

  render() {
    const passwordsValid = this.props.currentPasswordIsValid && this.props.newPasswordIsValid;
    if (!this.props.isChangingPassword) {
      return <Redirect to={RouteNames.accountSettings()} />;
    } else {
      return (
        <div>
          <ScrollAwareContext>
            <ScrollAwareAppNavStep
              submitDisabled={!passwordsValid || this.state.isSubmitting}
              submit={this.onSubmitPasswordChange}
              cancel={this.props.goBack}
              color="white"
            />

            <ContentFrame>
              <ScrollAwareScroller>
                <AppHeaderScrollableText
                  id="settingsChangePwd.title.text"
                />
                <ContentWrapper>
                  <Card.CardShape>
                    <form onSubmit={this.onSubmitPasswordChange}>
                      <Card.CardContent>
                        <Inputs.PasswordInput
                          value={this.props.currentPassword}
                          onFocus={this.handleOnFocus}
                          onChange={this.props.onChangeCurrentPassword}
                          isInvalid={false}
                          name="current_password"
                          label={<FormattedMessage id="settingsChangePwd.currentPassword.field.label" />}
                          initialFocus
                        />
                      </Card.CardContent>

                      <Card.CardContent>
                        <Inputs.PasswordInput
                          value={this.props.newPassword}
                          onFocus={() => {
                            this.setState({ isEnteringNewPassword: true });
                            this.handleOnFocus();
                          }}
                          onBlur={() => {
                            this.setState({ isEnteringNewPassword: false });
                          }}
                          onChange={this.onChangeNewPassword}
                          isInvalid={false}
                          name="new_password"
                          label={<FormattedMessage id="settingsChangePwd.newPassword.field.label" />}
                        />

                        <Spacer.Height18 />

                        <PasswordStrengthMeter value={this.props.passwordStrength} />
                      </Card.CardContent>
                    </form>
                  </Card.CardShape>
                </ContentWrapper>
              </ScrollAwareScroller>
            </ContentFrame>
          </ScrollAwareContext>
        </div>
      );
    }
  }
}
ChangePassword.propTypes = {
  currentCredentials: PropTypes.object,
  isChangingPassword: PropTypes.bool.isRequired,
  currentPassword: PropTypes.string.isRequired,
  passwordStrength: PropTypes.number.isRequired,
  newPassword: PropTypes.string.isRequired,
  currentPasswordIsValid: PropTypes.bool.isRequired,
  newPasswordIsValid: PropTypes.bool.isRequired,
  isMfaEnabled: PropTypes.bool.isRequired,
  passwordSubmitSuccessful: PropTypes.bool.isRequired,
  onChangeCurrentPassword: PropTypes.func.isRequired,
  onChangeNewPassword: PropTypes.func.isRequired,
  submitPasswordChange: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  goToPasswordWithMfa: PropTypes.func,
  finishPasswordChange: PropTypes.func,
  dismissNotification: PropTypes.func,
  verifyCredentials: PropTypes.func,
};

export default ChangePassword;
